/**
 * SpawnBatchList Component
 *
 * List view for spawn batches with filtering, status management, and batch actions.
 * Shows both real spawn batches and shadow spawn requirements from production timeline.
 * Uses a grouped table organized by strain with collapsible headers.
 * Part of US-P2-002: Spawn Production Tracking
 */

import { useState, useMemo } from 'react'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Label } from '@/components/ui/label'
import SearchInput from '@/components/ui/search-input'
import ToggleSwitch from '@/components/ToggleSwitch'
import { useSpawnBatches, useSpawnMutations } from '@/hooks/useSpawnBatches'
import { useStrains } from '@/hooks/useLC'
import { useShadowSpawnBatches } from '@/hooks/useShadowSpawnBatches'
import { toLocalDateString } from '@/utils/dateUtils'
import SpawnBatchForm from './SpawnBatchForm'
import SpawnBatchDetailModal from './SpawnBatchDetailModal'
import ThirdPartySpawnModal from './ThirdPartySpawnModal'
import SpawnBatchGroupedTable from './SpawnBatchGroupedTable'
import SpawnAddActionDialog from './SpawnAddActionDialog'
import CreateSessionWizard from './CreateSessionWizard'
import { SpawnSessionsList, SpawnSessionView } from './SpawnSessionView'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Plus,
  AlertTriangle,
  Filter,
  Calendar
} from 'lucide-react'

// Status options for dropdown filter
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'all', label: 'All Statuses' },
  { value: 'shadow', label: 'Planned' },
  { value: 'colonising', label: 'Colonising' },
  { value: 'ready', label: 'Ready' },  // Matches colonised + storage
  { value: 'used', label: 'Used' },
  { value: 'contaminated', label: 'Contaminated' },
  { value: 'discarded', label: 'Discarded' }
]

// Statuses considered "active"
const ACTIVE_STATUSES = ['colonising', 'colonised', 'storage']

export default function SpawnBatchList({ user, onLogout }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [strainFilter, setStrainFilter] = useState('all')
  const [showShadowBatches, setShowShadowBatches] = useState(false)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isThirdPartyOpen, setIsThirdPartyOpen] = useState(false)
  const [editingBatch, setEditingBatch] = useState(null)
  const [detailModalBatch, setDetailModalBatch] = useState(null)
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false)
  const [isSessionWizardOpen, setIsSessionWizardOpen] = useState(false)
  const [isSessionsListOpen, setIsSessionsListOpen] = useState(false)
  const [selectedSessionId, setSelectedSessionId] = useState(null)

  const { spawnBatches, loading, error, refetch } = useSpawnBatches()
  const {
    shadowSpawnBatches: shadowBatches,
    loading: shadowLoading,
    refetch: refetchShadow
  } = useShadowSpawnBatches()
  const { strains } = useStrains()
  const { updateSpawnBatch, convertShadowToSpawn, loading: mutationLoading } = useSpawnMutations()

  // Combine real spawn batches and shadow batches
  const allBatches = useMemo(() => {
    const realBatches = (spawnBatches || []).map(batch => ({
      ...batch,
      is_shadow: false,
      spawn_start: batch.inoculation_date,
      strain_code: batch.strain?.code,
      strain_name: batch.strain?.name,
      strain_origin: batch.strain?.origin
    }))

    const normalizedShadow = (shadowBatches || []).map(batch => ({
      ...batch,
      strain_code: batch.strains?.code,
      strain_name: batch.strains?.name,
      strain_origin: batch.strains?.origin,
      spawn_start: batch.inoculation_date
    }))

    return [...realBatches, ...normalizedShadow]
  }, [spawnBatches, shadowBatches])

  // Filter and search logic
  const filteredBatches = useMemo(() => {
    let result = [...allBatches]

    // Shadow batch toggle filter
    if (!showShadowBatches && statusFilter !== 'shadow') {
      result = result.filter(batch => !batch.is_shadow)
    }

    // Status filter
    if (statusFilter === 'active') {
      result = result.filter(batch =>
        !batch.is_shadow && ACTIVE_STATUSES.includes(batch.status)
      )
    } else if (statusFilter === 'shadow') {
      result = result.filter(batch => batch.is_shadow)
    } else if (statusFilter === 'ready') {
      result = result.filter(batch =>
        !batch.is_shadow && ['colonised', 'storage'].includes(batch.status)
      )
    } else if (statusFilter !== 'all') {
      result = result.filter(batch => !batch.is_shadow && batch.status === statusFilter)
    }

    // Strain filter
    if (strainFilter !== 'all') {
      result = result.filter(batch => batch.strain_id === strainFilter)
    }

    // Search
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      result = result.filter(batch =>
        batch.code?.toLowerCase().includes(search) ||
        batch.batch_code?.toLowerCase().includes(search) ||
        batch.strain_name?.toLowerCase().includes(search) ||
        batch.grain_type?.toLowerCase().includes(search)
      )
    }

    return result
  }, [allBatches, statusFilter, strainFilter, searchTerm, showShadowBatches])

  const handleAddBatch = () => {
    setEditingBatch(null)
    setIsFormOpen(true)
  }

  const handleAddThirdParty = () => {
    setIsThirdPartyOpen(true)
  }

  const handleActionSelect = (action) => {
    setIsActionDialogOpen(false)
    if (action === 'batch') handleAddBatch()
    else if (action === 'inventory') handleAddThirdParty()
    else if (action === 'session') setIsSessionWizardOpen(true)
  }

  const handleSessionCreated = (session) => {
    refetch()
    setSelectedSessionId(session?.id || null)
    setIsSessionsListOpen(true)
  }

  const handleSelectSession = (sessionId) => {
    setSelectedSessionId(sessionId)
  }

  const handleFormSave = async () => {
    setIsFormOpen(false)
    refetch()
  }

  const handleThirdPartySave = async () => {
    setIsThirdPartyOpen(false)
    refetch()
  }

  // Start a shadow batch (convert to colonising)
  const handleStartShadow = async (batch) => {
    try {
      const currentCode = batch.code || batch.batch_code
      const newCode = currentCode?.startsWith('SB-')
        ? currentCode.slice(3)
        : currentCode

      await updateSpawnBatch({
        id: batch.id,
        code: newCode,
        status: 'colonising',
        is_shadow: false,
        inoculation_date: toLocalDateString(new Date())
      })
      refetch()
      refetchShadow()
      setDetailModalBatch(null)
    } catch (error) {
      console.error('Failed to start shadow batch:', error)
    }
  }

  // Update status from inline dropdown or detail modal
  const handleStatusChange = async (batchId, newStatus) => {
    try {
      await updateSpawnBatch({ id: batchId, status: newStatus })
      refetch()
      setDetailModalBatch(null)
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  const isLoading = loading || shadowLoading

  if (isLoading) {
    return (
      <PageLayout
        title="Spawn Batches"
        user={user}
        onLogout={onLogout}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-content-secondary">Loading...</div>
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout
        title="Spawn Batches"
        user={user}
        onLogout={onLogout}
      >
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading spawn batches: {error}
          </AlertDescription>
        </Alert>
      </PageLayout>
    )
  }

  // Header actions
  const headerActions = (
    <div className="flex items-center gap-2">
      <div className="flex items-center space-x-3">
        <ToggleSwitch
          id="show-shadow"
          checked={showShadowBatches}
          onCheckedChange={setShowShadowBatches}
        />
        <Label htmlFor="show-shadow" className="cursor-pointer text-sm font-semibold">
          Show Planned
        </Label>
      </div>

      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-40">
          <Filter className="h-4 w-4 mr-2" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={strainFilter} onValueChange={setStrainFilter}>
        <SelectTrigger className="w-40">
          <SelectValue placeholder="All Strains" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Strains</SelectItem>
          {strains.map(strain => (
            <SelectItem key={strain.id} value={strain.id}>
              {strain.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button onClick={() => setIsSessionsListOpen(true)} variant="outline">
        <Calendar className="h-4 w-4 mr-2" />
        Sessions
      </Button>

      <Button onClick={() => setIsActionDialogOpen(true)} variant="outline">
        <Plus className="h-4 w-4 mr-2" />
        Add Spawn
      </Button>

      <div className="w-64">
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search..."
        />
      </div>
    </div>
  )

  return (
    <PageLayout
      title="Spawn Batches"
      user={user}
      onLogout={onLogout}
      headerActions={headerActions}
    >
      <div className="h-full overflow-y-auto">
        <SpawnBatchGroupedTable
          batches={filteredBatches}
          onBatchClick={(batch) => setDetailModalBatch(batch)}
          onStartShadow={handleStartShadow}
        />
      </div>

      {/* Form Dialog */}
      {isFormOpen && (
        <SpawnBatchForm
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSave={handleFormSave}
          editingBatch={editingBatch}
        />
      )}

      {/* Batch Detail Modal */}
      {detailModalBatch && (
        <SpawnBatchDetailModal
          isOpen={!!detailModalBatch}
          onClose={() => setDetailModalBatch(null)}
          batch={detailModalBatch}
          onStatusChange={handleStatusChange}
          onStartShadow={handleStartShadow}
          onBatchUpdate={refetch}
          isLoading={mutationLoading}
        />
      )}

      {/* Third-Party Spawn Modal */}
      <ThirdPartySpawnModal
        open={isThirdPartyOpen}
        onOpenChange={setIsThirdPartyOpen}
        onSave={handleThirdPartySave}
      />

      {/* Action Launcher Dialog */}
      <SpawnAddActionDialog
        open={isActionDialogOpen}
        onOpenChange={setIsActionDialogOpen}
        onSelectAction={handleActionSelect}
      />

      {/* Create Session Wizard */}
      <CreateSessionWizard
        open={isSessionWizardOpen}
        onOpenChange={setIsSessionWizardOpen}
        onSessionCreated={handleSessionCreated}
      />

      {/* Sessions List/Detail Modal */}
      <Dialog open={isSessionsListOpen} onOpenChange={(open) => {
        setIsSessionsListOpen(open)
        if (!open) setSelectedSessionId(null)
      }}>
        <DialogContent className="odin-modal-large">
          <DialogHeader>
            <DialogTitle>
              {selectedSessionId ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mr-2 -ml-2"
                  onClick={() => setSelectedSessionId(null)}
                >
                  ← Back
                </Button>
              ) : null}
              {selectedSessionId ? 'Session Detail' : 'Spawn Sessions'}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto">
            {selectedSessionId ? (
              <SpawnSessionView
                sessionId={selectedSessionId}
                onClose={() => setSelectedSessionId(null)}
              />
            ) : (
              <SpawnSessionsList onSelectSession={handleSelectSession} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </PageLayout>
  )
}
